import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';

import { AppRoutePath } from '@falcon/app/app.routing.model';
import {
  CompanyRegistrationRecord,
  RegistrationStatus,
  RegistrationStep,
} from '@falcon/app/core/api/company-registration/company-registration.model';
import { CompanyRegistrationService } from '@falcon/app/core/api/company-registration/company-registration.service';
import { skipErrorDialog } from '@falcon/app/core/interceptors/skip-error-dialog.context';
import { AuthStore } from '@falcon/app/core/storage/auth/auth.store';
import { UserDetailsModel } from '@falcon/app/shared/models/user.model';

import { CompanyRegistrationStateService } from './company-registration-state.service';
import {
  CompanyRegistrationRoutePath,
  isStoppedAtRepresentativeCheck,
  isStoppedByUnderAge,
  REGISTRATION_STEP_ROUTES,
} from './company-registration.model';

/** A wizard that is neither finished nor rejected can still be picked up by its owner. */
const RESUMABLE_STATUSES: RegistrationStatus[] = [
  'IN_PROGRESS',
  'REQUIRE_MANUAL_VALIDATION',
  'MANDATE_SETUP',
];

const POST_WIZARD_STEPS: RegistrationStep[] = ['MANDATE_SETUP', 'PENDING_ADMIN_VALIDATION'];

/** Steps completed before the account exists — nobody can log in while still on one of them. */
const PRE_ACCOUNT_STEPS: RegistrationStep[] = [
  'INITIAL',
  'EMAIL_VERIFICATION',
  'PASSWORD_CREATION',
];

@Injectable({ providedIn: 'root' })
export class CompanyRegistrationResumeService {
  readonly #router = inject(Router);
  readonly #authStore = inject(AuthStore);
  readonly #api = inject(CompanyRegistrationService);
  readonly #state = inject(CompanyRegistrationStateService);

  /** Route a just-authenticated company user: into their unfinished wizard, else to their company. */
  navigateAfterLogin(user: UserDetailsModel): void {
    this.#findRegistration().subscribe(record => {
      if (record?.status === 'REJECTED') {
        this.#router.navigate([
          '/',
          AppRoutePath.COMPANY_REGISTRATION,
          CompanyRegistrationRoutePath.REJECTED,
        ]);

        return;
      }

      if (record && isResumable(record)) {
        this.#hydrateState(record);
        this.#router.navigate(this.#resumeCommands(record));

        return;
      }

      this.#router.navigateByUrl(`${AppRoutePath.COMPANY}/${user.managedCompanyId}`);
    });
  }

  #findRegistration(): Observable<CompanyRegistrationRecord | null> {
    // Admins are never mid-onboarding, and `/current` would answer for their own e-mail anyway.
    if (!this.#authStore.isCompanyUser() || this.#authStore.isAdmin()) return of(null);

    // A failed lookup must never cost the user their login.
    return this.#api.getCurrentRegistration(skipErrorDialog()).pipe(
      map((record): CompanyRegistrationRecord | null =>
        record.status === 'REQUIRE_MANUAL_VALIDATION' && isPastWizard(record)
          ? { ...record, nextStep: 'PENDING_ADMIN_VALIDATION' }
          : record
      ),
      catchError(() => of(null))
    );
  }

  #hydrateState(record: CompanyRegistrationRecord): void {
    if (!record.registrationId) return;

    this.#state.setContact(
      record.registrationId,
      record.owner?.email ?? '',
      record.company?.vat ?? ''
    );
    this.#state.setCompany(record.company ?? null);

    const { firstName, lastName, birthDate } = record.owner ?? {};
    if (firstName && lastName && birthDate) {
      this.#state.setIdentity({ firstName, lastName, dateOfBirth: birthDate });
    }

    if (record.company?.pcCode) {
      this.#state.setPcCode(record.company.pcCode);
    }
  }

  #resumeCommands(record: CompanyRegistrationRecord): string[] {
    const path = resumePath(record);

    // The itsme and personal-data screens re-read the registration when its id is in the URL, which
    // is exactly what a wizard resumed in a fresh browser session needs.
    const carriesId =
      path === CompanyRegistrationRoutePath.IDENTITY ||
      path === CompanyRegistrationRoutePath.USER_DETAILS;

    return [
      '/',
      AppRoutePath.COMPANY_REGISTRATION,
      ...(carriesId && record.registrationId ? [record.registrationId] : []),
      path,
    ].filter(Boolean);
  }
}

/**
 * `currentStep` is the step the user finished, `nextStep` the one they owe us, so the latter is
 * where the wizard picks up again.
 */
function resumeStep(record: CompanyRegistrationRecord): RegistrationStep {
  const step = record.nextStep ?? record.currentStep;

  return !step || PRE_ACCOUNT_STEPS.includes(step) ? 'ITS_ME_VERIFICATION' : step;
}

/**
 * Neither stop screen is a BE step. An under-age registration is terminal; one stopped at the
 * representative check keeps landing there on every login until an admin unblocks it.
 */
function resumePath(record: CompanyRegistrationRecord): CompanyRegistrationRoutePath {
  if (isStoppedByUnderAge(record)) return CompanyRegistrationRoutePath.UNDER_AGE;

  return isStoppedAtRepresentativeCheck(record)
    ? CompanyRegistrationRoutePath.STOPPED
    : REGISTRATION_STEP_ROUTES[resumeStep(record)];
}

function isResumable(record: CompanyRegistrationRecord): boolean {
  return !!record.status && RESUMABLE_STATUSES.includes(record.status);
}

function isPastWizard(record: CompanyRegistrationRecord): boolean {
  const step = record.nextStep ?? record.currentStep;

  return !step || POST_WIZARD_STEPS.includes(step);
}
